import { formatPhoneNumber } from "react-phone-number-input";
import { parseUserSettings } from "./userHelpers";
import {
  PHONE_USAGE_VALUES,
  PHONE_USAGE_TOOLTIPS,
} from "../components/commonComponents/PhoneNumbersField";

/**
 * Returns the lead's primary phone number, falling back to the first phone number
 * in the list if no phone is marked primary.
 *
 * @param {{ phoneNumbers?: Array<{ phone?: string, isPrimary?: boolean }> }} lead
 * @param {{ withPlusPrefix?: boolean }} [options] - When `withPlusPrefix` is true,
 *   ensures the returned number starts with "+".
 * @returns {string|undefined}
 */
export const getLeadToPhone = (lead, { withPlusPrefix = false } = {}) => {
  const phone =
    lead?.phoneNumbers?.find(p => p?.isPrimary)?.phone || lead?.phoneNumbers?.[0]?.phone;
  if (!phone) return phone;
  return withPlusPrefix && !phone.startsWith("+") ? `+${phone}` : phone;
};

/**
 * Build the read-only phone display values + tooltips from a user object.
 * Reads PHONE_NUMBERS from user.settings (falls back to user.phone).
 *
 * @returns {{ values: Array<{ number: string, rest: string }>, tooltips: string[] }}
 */
export const formatPhoneDisplay = user => {
  const formatUsedFor = usedForArray => {
    if (!Array.isArray(usedForArray) || usedForArray.length === 0) {
      return `All (${PHONE_USAGE_VALUES.join(", ")})`;
    }
    if (usedForArray.includes("All")) {
      return `All (${PHONE_USAGE_VALUES.join(", ")})`;
    }
    return usedForArray.join(", ");
  };

  const generateTooltip = (phone, index) => {
    const isPrimary = phone.isPrimary || index === 0;
    const type = phone.type || "Personal";
    const usedForArray =
      Array.isArray(phone.usedFor) && phone.usedFor.length > 0 ? phone.usedFor : ["All"];
    const usageDescriptions = usedForArray.map(
      usage => PHONE_USAGE_TOOLTIPS[usage] || PHONE_USAGE_TOOLTIPS.Others
    );
    const phoneTitle = `${isPrimary ? "Primary" : "Additional"} ${type} Phone Number`;
    return `${phoneTitle}: ${usageDescriptions.join(" ")}`;
  };

  const parsedSettings = parseUserSettings(user?.settings);

  if (
    parsedSettings?.PHONE_NUMBERS &&
    Array.isArray(parsedSettings.PHONE_NUMBERS) &&
    parsedSettings.PHONE_NUMBERS.length > 0
  ) {
    const values = parsedSettings.PHONE_NUMBERS.map(phone => {
      const type = phone.type || "Personal";
      return {
        number: formatPhoneNumber(phone.number),
        rest: `(${type}) For ${formatUsedFor(phone.usedFor)}`,
      };
    });
    const tooltips = parsedSettings.PHONE_NUMBERS.map((phone, index) =>
      generateTooltip(phone, index)
    );
    return { values, tooltips };
  }

  if (user?.phone) {
    const phoneToDisplay = {
      number: user.phone,
      type: "Personal",
      usedFor: ["All"],
      isPrimary: true,
    };
    return {
      values: [
        {
          number: formatPhoneNumber(phoneToDisplay.number),
          rest: `(Personal) For ${formatUsedFor(phoneToDisplay.usedFor)}`,
        },
      ],
      tooltips: [generateTooltip(phoneToDisplay, 0)],
    };
  }

  return { values: [], tooltips: [] };
};
